import React, { useState, useRef, useEffect } from 'react';
import { RotateCw, ZoomIn, ZoomOut, RefreshCcw, AlertTriangle, Loader2 } from 'lucide-react';
import './ImageEditor.css';

const ImageEditor = ({
  imageUrl,
  onSave,
  onCancel,
  className = '',
  size = 120,
  saving = false,
}) => {
  const [rotation, setRotation] = useState(0);
  const [zoom, setZoom] = useState(1);
  const [loadError, setLoadError] = useState(null);
  const [loading, setLoading] = useState(false);
  const canvasRef = useRef(null);
  const imageRef = useRef(null);
  const loadedUrlRef = useRef(null);
  const busy = saving || loading || !!loadError;

  // Load and draw image when URL changes; redraw only when rotation/zoom changes.
  // Remote URLs are fetched via fetch() + Blob URL — this forces a fresh CORS
  // request (bypassing browser image cache that may hold a pre-CORS copy from
  // a prior <img src=URL> render elsewhere in the app), and the resulting blob
  // URL is same-origin so the canvas stays untainted without crossOrigin.
  useEffect(() => {
    if (!imageUrl || !canvasRef.current) return;

    // Same URL still in memory — this re-run is rotation/zoom; just redraw.
    if (loadedUrlRef.current === imageUrl && imageRef.current) {
      drawImage();
      return;
    }

    setLoadError(null);
    setLoading(true);
    let cancelled = false;
    let blobUrl = null;

    const load = async () => {
      try {
        let srcForImg = imageUrl;

        if (!imageUrl.startsWith('data:')) {
          // Cache-bust: appending a unique query param guarantees a fresh cache
          // entry, beating any pre-CORS-cached <img> copy from elsewhere in the app
          const bustedUrl = imageUrl + (imageUrl.includes('?') ? '&' : '?') + '_cb=' + Date.now();
          const response = await fetch(bustedUrl, {
            mode: 'cors',
            credentials: 'omit',
            cache: 'reload',
          });
          if (!response.ok) throw new Error(`HTTP ${response.status}`);
          const blob = await response.blob();
          if (cancelled) return;
          blobUrl = URL.createObjectURL(blob);
          srcForImg = blobUrl;
        }

        const image = new Image();
        image.onload = () => {
          if (cancelled) return;
          imageRef.current = image;
          loadedUrlRef.current = imageUrl;
          drawImage();
          setLoading(false);
        };
        image.onerror = () => {
          if (cancelled) return;
          console.error('Failed to render fetched image:', imageUrl);
          setLoadError(imageUrl);
          setLoading(false);
        };
        image.src = srcForImg;
      } catch (error) {
        if (cancelled) return;
        console.error('Image fetch failed:', imageUrl, error);
        setLoadError(imageUrl);
        setLoading(false);
      }
    };

    load();

    return () => {
      cancelled = true;
      if (blobUrl) URL.revokeObjectURL(blobUrl);
    };
  // eslint-disable-next-line react-hooks/exhaustive-deps -- re-run only when imageUrl, rotation, zoom change; drawImage is defined in render and reads the latest state
  }, [imageUrl, rotation, zoom]);

  const drawImage = () => {
    if (!canvasRef.current || !imageRef.current) return;

    const canvas = canvasRef.current;
    const ctx = canvas.getContext('2d');
    const image = imageRef.current;

    // Set canvas size
    canvas.width = size;
    canvas.height = size;

    // Clear canvas
    ctx.clearRect(0, 0, canvas.width, canvas.height);

    // Save context state
    ctx.save();

    // Move to center of canvas
    ctx.translate(canvas.width / 2, canvas.height / 2);

    // Apply rotation
    ctx.rotate((rotation * Math.PI) / 180);

    // Apply zoom
    ctx.scale(zoom, zoom);

    // Calculate dimensions to fit image in square canvas
    const scale = Math.max(canvas.width / image.width, canvas.height / image.height);
    const scaledWidth = image.width * scale;
    const scaledHeight = image.height * scale;

    // Draw image centered
    ctx.drawImage(
      image,
      -scaledWidth / 2,
      -scaledHeight / 2,
      scaledWidth,
      scaledHeight
    );

    // Restore context state
    ctx.restore();
  };

  const handleRotate = () => {
    setRotation((prev) => (prev + 90) % 360);
  };

  const handleZoomIn = () => {
    setZoom((prev) => Math.min(prev + 0.1, 3)); // Max 3x zoom
  };

  const handleZoomOut = () => {
    setZoom((prev) => Math.max(prev - 0.1, 0.5)); // Min 0.5x zoom
  };

  const handleReset = () => {
    setRotation(0);
    setZoom(1);
  };

  const handleSave = () => {
    if (!canvasRef.current) return;

    try {
      // Convert canvas to blob
      canvasRef.current.toBlob((blob) => {
        if (blob && onSave) {
          // Create a File object from the blob
          const file = new File([blob], 'edited-image.jpg', { type: 'image/jpeg' });
          onSave(file, canvasRef.current.toDataURL('image/jpeg', 0.9));
        }
      }, 'image/jpeg', 0.9);
    } catch (error) {
      console.error('Canvas is tainted, cannot export. Trying fallback...');
      // If canvas is tainted, we need to reload the image with proper CORS
      if (onSave && imageUrl) {
        // Just pass the original image URL back
        // The parent component will handle re-uploading
        fetch(imageUrl)
          .then(res => res.blob())
          .then(blob => {
            const file = new File([blob], 'edited-image.jpg', { type: 'image/jpeg' });
            onSave(file, imageUrl);
          })
          .catch(err => {
            console.error('Failed to save image:', err);
            alert('Failed to save image. Please try uploading a new image.');
          });
      }
    }
  };

  return (
    <div className={`image-editor ${className}`}>
      <div className="image-editor-preview" style={{ position: 'relative' }}>
        <canvas
          ref={canvasRef}
          className="image-editor-canvas"
          width={size}
          height={size}
        />
        {loading && !loadError && (
          <div
            style={{
              position: 'absolute',
              inset: 0,
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              justifyContent: 'center',
              gap: '8px',
              background: 'rgba(255, 255, 255, 0.85)',
              borderRadius: '50%',
              color: '#374151',
            }}
            aria-live="polite"
            aria-busy="true"
          >
            <Loader2 size={32} className="spinning" />
            <span style={{ fontSize: '12px' }}>Loading image…</span>
          </div>
        )}
        {loadError && (
          <div
            style={{
              position: 'absolute',
              inset: 0,
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              justifyContent: 'center',
              gap: '8px',
              padding: '16px',
              textAlign: 'center',
              background: 'rgba(255, 255, 255, 0.95)',
              borderRadius: '50%',
              color: '#b91c1c',
            }}
          >
            <AlertTriangle size={32} />
            <strong style={{ fontSize: '13px' }}>Image failed to load</strong>
            <span style={{ fontSize: '11px', wordBreak: 'break-all', maxHeight: '60px', overflow: 'hidden' }}>
              {loadError}
            </span>
            <span style={{ fontSize: '11px', opacity: 0.75 }}>
              Open DevTools Console for the exact error
            </span>
          </div>
        )}
      </div>

      <div className="image-editor-controls">
        <button
          type="button"
          onClick={handleRotate}
          className="image-editor-btn"
          disabled={busy}
          title="Rotate 90°"
        >
          <RotateCw size={18} />
        </button>

        <button
          type="button"
          onClick={handleZoomOut}
          className="image-editor-btn"
          disabled={busy}
          title="Zoom Out"
        >
          <ZoomOut size={18} />
        </button>

        <button
          type="button"
          onClick={handleZoomIn}
          className="image-editor-btn"
          disabled={busy}
          title="Zoom In"
        >
          <ZoomIn size={18} />
        </button>

        <button
          type="button"
          onClick={handleReset}
          className="image-editor-btn"
          disabled={busy}
          title="Reset"
        >
          <RefreshCcw size={18} />
        </button>
      </div>

      <div className="image-editor-actions">
        <button
          type="button"
          onClick={onCancel}
          className="image-editor-cancel-btn"
          disabled={saving}
        >
          Cancel
        </button>
        <button
          type="button"
          onClick={handleSave}
          className="image-editor-save-btn"
          disabled={busy}
          aria-busy={saving}
          style={{ display: 'inline-flex', alignItems: 'center', gap: '8px', justifyContent: 'center' }}
        >
          {saving && <Loader2 size={14} className="spinning" />}
          {saving ? 'Saving…' : 'Apply Changes'}
        </button>
      </div>
    </div>
  );
};

export default ImageEditor;
